import { MoreVertical, CheckCircle, Loader2, Gem } from "lucide-react";
import { CompanyAvatar } from "./company-avatar";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { getErrorMessage } from "@/lib/api-error";
import { isIndustriesPendingApproval } from "@/lib/deal-field-state";
import { resolveIndustryLabel } from "@/lib/utils";
import { useWorkspaceStore } from "@/store/workspace-store";
import { dealsApi } from "@/api/deals-api";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { Link, useNavigate } from "react-router";
import { hasFitScore, getFitScore, getReportStatusIndicator } from "./utils";
import { useState } from "react";
import { DeleteDialog } from "./delete-dialog";
import { DeactivateDealDialog } from "./deactivate-deal-dialog";
import { AddToBoardSubMenu } from "./add-to-board-menu";
import { createDeal } from "@/api/deals-api";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";

interface DealCardProps {
  deal: DealTransformed;
  workspaceId: string;
  onDealDeleted?: () => void;
  onDealArchived?: () => void;
}

export const DealCard = ({
  deal,
  workspaceId,
  onDealDeleted,
  onDealArchived,
}: DealCardProps) => {
  const { user } = useUser();
  const { activeWorkspace } = useWorkspaceStore();
  const navigate = useNavigate();
  const portfolioStatus = activeWorkspace?.statuses.find((s) =>
    s.title.toLowerCase().includes("portfolio")
  )?.title;
  const statusIndicator = getReportStatusIndicator(
    deal.reportStatus?.opportunityApplication?.report_status
  );
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({ isOpen: false, dealId: "", dealName: "" });

  const [deactivateDialog, setDeactivateDialog] = useState<{
    isOpen: boolean;
    dealId: string;
    dealName: string;
  }>({ isOpen: false, dealId: "", dealName: "" });

  const handleArchive = async (dealId: string) => {
    if (!workspaceId) return;
    try {
      await createDeal.createFlaggedCompany(
        workspaceId,
        dealId,
        user?.id ?? ""
      );
      onDealArchived?.();
      toast.success("Deal archived successfully");
    } catch (error) {
      console.error("Failed to archive deal:", error);
      toast.error(getErrorMessage(error, "Failed to archive deal"));
    }
  };

  const handleAddToPortfolio = async (dealId: string) => {
    if (!workspaceId || !portfolioStatus) return;
    try {
      await dealsApi.changeStatus(
        dealId,
        portfolioStatus,
        user?.id ?? "",
        workspaceId
      );
      toast.success(`Moved to ${portfolioStatus}`);
      onDealArchived?.();
    } catch (error) {
      console.error("Failed to move deal to portfolio:", error);
      toast.error(getErrorMessage(error, "Failed to move deal to portfolio"));
    }
  };

  const industryLabels = Array.from(
    new Set(
      (deal.overview.industry ?? [])
        .map(resolveIndustryLabel)
        .filter((l): l is string => Boolean(l) && l !== "—")
    )
  );

  const fit = hasFitScore(deal) ? Math.round(getFitScore(deal) as number) : null;

  return (
    <>
      <div className="group rounded-md border border-border bg-card transition-colors hover:bg-foreground/[0.02]">
        {/* Header row: avatar + name + menu */}
        <div className="flex items-center gap-2 px-3 pt-2.5 pb-2">
          <Link
            to={`/${workspaceId}/deals/${deal.id}`}
            className="flex min-w-0 flex-1 items-center gap-2"
          >
            <CompanyAvatar
              companyFiles={deal.company_files}
              websiteUrl={deal.overview.website}
              alt={deal.overview.name}
            />
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate text-sm font-medium text-foreground group-hover:underline decoration-muted-foreground/30 underline-offset-2">
                {deal.overview.name}
              </span>
              {deal.aiTracked && (
                <Gem className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
              {deal.prospectiveAffEmail && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <CheckCircle className="h-3.5 w-3.5 shrink-0 text-green-500" />
                    </TooltipTrigger>
                    <TooltipContent>Verified company</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              {statusIndicator && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex shrink-0">
                        <statusIndicator.icon
                          className={`h-3.5 w-3.5 ${
                            statusIndicator.type === "loading"
                              ? "text-blue-500 animate-spin"
                              : "text-red-500"
                          }`}
                        />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>{statusIndicator.label}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </span>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 shrink-0 px-0 text-muted-foreground hover:text-foreground"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <AddToBoardSubMenu companyIds={[deal.id]} />
              {portfolioStatus && deal.overview.status !== portfolioStatus && (
                <DropdownMenuItem onClick={() => handleAddToPortfolio(deal.id)}>
                  Add to Portfolio
                </DropdownMenuItem>
              )}
              {deal.aiTracked && (
                <DropdownMenuItem
                  onClick={() =>
                    setDeactivateDialog({
                      isOpen: true,
                      dealId: deal.id,
                      dealName: deal.overview.name,
                    })
                  }
                >
                  Mark as not premium
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => handleArchive(deal.id)}
                className="text-yellow-600"
              >
                Archive
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() =>
                  setDeleteDialog({
                    isOpen: true,
                    dealId: deal.id,
                    dealName: deal.overview.name,
                  })
                }
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Industry chips */}
        {isIndustriesPendingApproval(deal) ? (
          <div className="flex flex-wrap items-center gap-1 px-3 pb-2">
            <Link
              to={`/${workspaceId}/deals/${deal.id}?tab=profile#field-industries`}
              className="inline-flex items-center gap-1.5 rounded-sm bg-purple-50 px-1.5 py-0.5 text-[11px] font-medium text-purple-700 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-200 dark:hover:bg-purple-900/40"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
              Review industries
            </Link>
          </div>
        ) : industryLabels.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1 px-3 pb-2">
            {industryLabels.slice(0, 2).map((label, i) => (
              <span
                key={i}
                className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-[11px] text-foreground"
              >
                {label}
              </span>
            ))}
            {industryLabels.length > 2 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-[11px] text-muted-foreground cursor-default">
                      +{industryLabels.length - 2}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-[280px]">
                      {industryLabels.slice(2).join(", ")}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ) : null}

        {/* Footer row: fit score or run-analysis affordance */}
        <div className="flex items-center justify-between border-t border-border/60 px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
            Fit
          </span>
          {fit !== null ? (
            <span className="font-mono text-sm font-medium tabular-nums text-foreground">
              {fit}
              <span className="ml-0.5 font-normal text-muted-foreground/50">
                /100
              </span>
            </span>
          ) : statusIndicator?.type === "loading" ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
              <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
              Analyzing…
            </span>
          ) : statusIndicator?.type === "failed" ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${workspaceId}/deals/${deal.id}?tab=analysis`);
              }}
              className="text-xs text-red-500 hover:underline underline-offset-2 whitespace-nowrap"
            >
              Analysis failed
            </button>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/${workspaceId}/deals/${deal.id}?tab=analysis`);
              }}
              className="text-xs text-muted-foreground hover:text-foreground hover:underline underline-offset-2 whitespace-nowrap"
            >
              Run analysis →
            </button>
          )}
        </div>
      </div>

      <DeleteDialog
        isOpen={deleteDialog.isOpen}
        onClose={() =>
          setDeleteDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deleteDialog.dealId}
        dealName={deleteDialog.dealName}
        workspaceId={workspaceId}
        onSuccess={() => {
          onDealDeleted?.();
        }}
      />

      <DeactivateDealDialog
        isOpen={deactivateDialog.isOpen}
        onClose={() =>
          setDeactivateDialog({ isOpen: false, dealId: "", dealName: "" })
        }
        dealId={deactivateDialog.dealId}
        dealName={deactivateDialog.dealName}
        workspaceId={workspaceId}
      />
    </>
  );
};
